import { useCallback, useEffect, useRef, useState, type ComponentType } from 'react';
import { MessageCircle as LucideMessageCircle } from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import type { WaitingState } from '../../engine/stepEngine';
import { evaluateSpotGateAnswer } from '../../engine/stepEngine';
import { audioEngine } from '../../audio/audioEngine';
import { vibratePreset } from '../../audio/haptics';
import { readDomMood } from '../../minigames/foundation/hooks/readDomMood';

/** P8-1: spot_gate 正解時の承認ビート（骨董パネル上の短い受け止め）の長さ。
 *  600ms 前後・タップでスキップ可・800ms を上限とし、進行を絶対にブロックしない。
 *  reduced-motion では大幅に短縮する（演出弱化は CSS 側でも担保）。 */
const SPOT_GATE_APPROVAL_HOLD_MS = 620;
const SPOT_GATE_APPROVAL_HOLD_REDUCED_MS = 220;
const SPOT_GATE_APPROVAL_HOLD_MAX_MS = 800;

// P8-2a（P7-5 の適用漏れ）: free_input の絵文字 💬 を lucide へ統一
//（TS2786 回避の正規化は foundation/primitives/Icon.tsx と同じ流儀）。
const MessageCircle = LucideMessageCircle as unknown as ComponentType<LucideProps>;

interface StepInteractionOverlayProps {
  waiting: WaitingState;
  isBusy: boolean;
  error?: string | null;
  onCompleteReward: (endingId?: string) => Promise<void>;
  onSubmitFreeInput: (payload: {
    userText: string;
    suggestedRouteKey?: string;
    inputMeta?: {
      charCount: number;
      isSilent?: boolean;
      isTooShort?: boolean;
      isNonsense?: boolean;
    };
  }) => Promise<void>;
  onSkipFreeInput: (routeKey?: string) => Promise<void>;
  onSelectDialogChoice: (choiceIndex: number) => Promise<void>;
  onSubmitSpotGateAnswer: (answer: string) => Promise<void>;
}

export function StepInteractionOverlay({
  waiting,
  isBusy,
  error,
  onCompleteReward,
  onSubmitFreeInput,
  onSkipFreeInput,
  onSelectDialogChoice,
  onSubmitSpotGateAnswer
}: StepInteractionOverlayProps) {
  // P8-2a: 旧 branch 選択 UI は削除した。stepEngine.computeWaiting は branch step で
  // waiting を返さず advance() 内で自動評価されるため（stepEngine.ts）、このコンポーネントに
  // waiting.type === 'branch' が渡ることはなく、到達不能の死コードだった（反証確認済み）。

  if (waiting.type === 'reward') {
    return <RewardOverlay waiting={waiting} isBusy={isBusy} onCompleteReward={onCompleteReward} />;
  }

  if (waiting.type === 'free_input') {
    return (
      <FreeInputOverlay
        waiting={waiting}
        isBusy={isBusy}
        error={error}
        onSubmitFreeInput={onSubmitFreeInput}
        onSkipFreeInput={onSkipFreeInput}
      />
    );
  }

  if (waiting.type === 'spot_gate') {
    return (
      <SpotGateOverlay
        waiting={waiting}
        isBusy={isBusy}
        error={error}
        onSubmitSpotGateAnswer={onSubmitSpotGateAnswer}
      />
    );
  }

  // Phase A-h: ダイアログ選択肢
  if (waiting.type === 'dialog_choice') {
    return (
      <div className="interaction-overlay">
        <div className="dialog-choice-overlay">
          <div className="dialog-choice-panel">
            <div className="dialog-choice-buttons">
              {waiting.choices.map((choice, index) => (
                <button
                  key={index}
                  className="dialog-choice-btn"
                  disabled={isBusy}
                  onClick={() => {
                    void onSelectDialogChoice(index);
                  }}
                >
                  {choice.text}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // LINE Dialog: LineDialogView自体がUI内で選択肢/入力を管理するため、
  // ここではオーバーレイは不要（LineDialogViewのコールバック経由でランタイムに通知）
  if (waiting.type === 'line_dialog') {
    return null;
  }

  return null;
}

function SpotGateOverlay({
  waiting,
  isBusy,
  error,
  onSubmitSpotGateAnswer
}: {
  waiting: Extract<WaitingState, { type: 'spot_gate' }>;
  isBusy: boolean;
  error?: string | null;
  onSubmitSpotGateAnswer: (answer: string) => Promise<void>;
}) {
  const [answer, setAnswer] = useState('');
  const [lastRejectedAttempt, setLastRejectedAttempt] = useState<number | null>(null);
  // P8-1: 承認ビート中の状態。非 null の間は骨董パネル上に印章演出を出し、
  // タイマー満了 or タップスキップでエンジンへ解答を送って次 step へ進む。
  const [approval, setApproval] = useState<{ submitted: string } | null>(null);
  const approvalDoneRef = useRef(false);
  const approvalTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setAnswer('');
    setLastRejectedAttempt(null);
    setApproval(null);
    approvalDoneRef.current = false;
    if (approvalTimerRef.current !== null) {
      clearTimeout(approvalTimerRef.current);
      approvalTimerRef.current = null;
    }
  }, [waiting.step.id]);

  // unmount 時にビートのタイマーを必ず掃除する。
  useEffect(
    () => () => {
      if (approvalTimerRef.current !== null) clearTimeout(approvalTimerRef.current);
    },
    []
  );

  // ビートを終了してエンジンへ解答を送る（多重呼び出しは ref で遮断＝連打・タップスキップ安全）。
  const finishApproval = useCallback(
    (submitted: string) => {
      if (approvalDoneRef.current) return;
      approvalDoneRef.current = true;
      if (approvalTimerRef.current !== null) {
        clearTimeout(approvalTimerRef.current);
        approvalTimerRef.current = null;
      }
      void onSubmitSpotGateAnswer(submitted)
        .then(() => setAnswer(''))
        .catch(() => {
          // 送信失敗時はビートを解除して再操作可能に戻す（進行を塞がない）。
          approvalDoneRef.current = false;
          setApproval(null);
        });
    },
    [onSubmitSpotGateAnswer]
  );

  const hints = waiting.step.hints ?? [];
  const visibleHints = hints.slice(0, Math.min(waiting.attempts, hints.length));
  // WYSIWYG: 保存値をそのまま表示する（整形・地名除去・スポット→場所置換は廃止）。
  // 表示用の整形は撤去済みのため、title/prompt は素の保存値を使う。
  const displayTitle = waiting.step.title;
  const displayPrompt = waiting.step.prompt;
  const inputLabel = getSpotGateInputLabel(waiting.step);
  const inputPlaceholder = getSpotGateInputPlaceholder(waiting.step);
  const spotClass = getSpotGateToneClass(waiting.step.spotId);
  const maxAttempts = waiting.step.maxAttempts ?? null;
  const showRejected = lastRejectedAttempt !== null && waiting.attempts >= lastRejectedAttempt;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const progressPct = maxAttempts ? Math.min(100, Math.round((waiting.attempts / maxAttempts) * 100)) : 0;

  useEffect(() => {
    if (isBusy) return;
    inputRef.current?.focus();
  }, [isBusy, waiting.step.id]);

  const handleSubmit = () => {
    const submitted = answer.trim();
    if (!submitted || isBusy || approval) return;
    const mood = readDomMood();

    // P8-1: 照合結果をエンジンと同一の純関数（evaluateSpotGateAnswer）で先読みする。
    // 実際の進行・attempts 加算は従来どおり onSubmitSpotGateAnswer（エンジン）だけが行う。
    if (!evaluateSpotGateAnswer(waiting.step, submitted)) {
      // 不一致: 小さな否定音のみ（演出は追加しない）。従来どおり即時にエンジンへ送る。
      audioEngine.playSfx('fail', { mood, volume: 0.4 });
      setLastRejectedAttempt(waiting.attempts + 1);
      void onSubmitSpotGateAnswer(submitted).then(() => setAnswer(''));
      return;
    }

    // 一致: 即カットせず、骨董パネル上の承認ビート（印章・SFX・ハプティクス）を挟んでから進む。
    setApproval({ submitted });
    audioEngine.playSfx('success', { mood, volume: 0.7 });
    vibratePreset('success');
    const reduceMotion =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const holdMs = Math.min(
      reduceMotion ? SPOT_GATE_APPROVAL_HOLD_REDUCED_MS : SPOT_GATE_APPROVAL_HOLD_MS,
      SPOT_GATE_APPROVAL_HOLD_MAX_MS
    );
    approvalTimerRef.current = setTimeout(() => {
      approvalTimerRef.current = null;
      finishApproval(submitted);
    }, holdMs);
  };

  return (
    <div
      className={`interaction-overlay spot-gate-overlay ${spotClass}${approval ? ' spot-gate-overlay--approved' : ''}`}
      // P8-1: ビート中はどこをタップしても即座に次へ（スキップ）。進行をブロックしない。
      onClickCapture={approval ? () => finishApproval(approval.submitted) : undefined}
    >
      <div className="spot-gate-atmosphere" aria-hidden="true">
        <span />
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
      <form
        className="spot-gate-card"
        onSubmit={(event) => {
          event.preventDefault();
          handleSubmit();
        }}
      >
        <div className="spot-gate-roof" aria-hidden="true" />
        <div className="spot-gate-board">
          <div className="spot-gate-header">
            <p className="spot-gate-crest">現地照合</p>
          </div>
          <h3>{displayTitle}</h3>
          <div className="spot-gate-divider" aria-hidden="true" />
          <div className="spot-gate-parchment">
            <p>{displayPrompt}</p>
          </div>
          <label className="spot-gate-label">
            <span className="spot-gate-label-row">
              <span className="spot-gate-label-seal" aria-hidden="true" />
              {inputLabel}
            </span>
            <span className="spot-gate-water-frame">
              <input
                ref={inputRef}
                value={answer}
                onChange={(event) => setAnswer(event.target.value)}
                disabled={isBusy || approval !== null}
                placeholder={inputPlaceholder}
                autoComplete="off"
              />
              <span className="spot-gate-water-ring" aria-hidden="true" />
            </span>
          </label>
          {maxAttempts && (
            <div className="spot-gate-progress" aria-hidden="true">
              <span style={{ width: `${progressPct}%` }} />
            </div>
          )}
          <div className="spot-gate-meta" aria-label="試行回数">
            <span><b>試行</b> <strong>{waiting.attempts}{maxAttempts ? `/${maxAttempts}` : ''}</strong></span>
            {waiting.remainingAttempts !== null && waiting.remainingAttempts > 0 && (
              <span><b>残り</b> <strong>{waiting.remainingAttempts}</strong></span>
            )}
          </div>
          {showRejected && (
            <p className="spot-gate-error" role="alert">
              {waiting.step.inputMode === 'keyword'
                ? 'まだ一致していません。手がかりから合言葉をもう一度思い出してください。'
                : 'まだ一致していません。現地の文字や目印をもう一度見直してください。'}
            </p>
          )}
          {error && <p className="spot-gate-error" role="alert">{error}</p>}
          {visibleHints.length > 0 && (
            <div className="spot-gate-hints">
              <p className="eyebrow subtle">手がかり</p>
              {visibleHints.map((hint, index) => (
                <p key={`${hint}-${index}`}>{hint}</p>
              ))}
            </div>
          )}
          <button
            type="submit"
            className="primary-btn spot-gate-submit"
            disabled={isBusy || approval !== null || answer.trim().length === 0}
          >
            照合する
          </button>
          {/* P8-1: 承認ビート（正解の受け止め）。骨董パネル中央に印章がふっと灯り、波紋が広がる。
              pointer-events:none（スキップはオーバーレイ側の onClickCapture が受ける）。 */}
          {approval && (
            <>
              <div className="spot-gate-approval" aria-hidden="true">
                <span className="spot-gate-approval__ripple" />
                <span className="spot-gate-approval__seal">合</span>
              </div>
              <span className="spot-gate-approval-sr" role="status">
                照合しました
              </span>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

function getSpotGateInputLabel(step: Extract<WaitingState, { type: 'spot_gate' }>['step']): string {
  if (step.title.includes('名前を呼ぶ')) {
    return '呼び名';
  }
  if (step.inputMode === 'observation') {
    return '観察したこと';
  }
  if (step.inputMode === 'either') {
    return '確認した場所または観察';
  }
  if (step.inputMode === 'keyword') {
    return '合言葉';
  }
  return '確認した場所';
}

function getSpotGateInputPlaceholder(step: Extract<WaitingState, { type: 'spot_gate' }>['step']): string {
  if (step.title.includes('名前を呼ぶ')) {
    return '呼び名を入力';
  }
  if (step.inputMode === 'keyword') {
    return '合言葉を入力';
  }
  return '現地で見つけた名前を入力';
}

function getSpotGateToneClass(spotId?: string): string {
  const key = String(spotId ?? 'default').replace(/[^a-zA-Z0-9_-]/g, '-');
  return `spot-gate-overlay--${key}`;
}

function RewardOverlay({
  waiting,
  isBusy,
  onCompleteReward
}: {
  waiting: Extract<WaitingState, { type: 'reward' }>;
  isBusy: boolean;
  onCompleteReward: (endingId?: string) => Promise<void>;
}) {
  const [endingId, setEndingId] = useState(waiting.step.id);

  return (
    <div className="interaction-overlay">
      <div className="interaction-card reward">
        {/* P8-3「シェルの声」: 英語ラベル（Reward / Ending ID）とシステム語（クリア確定）を
            世界内語彙へ。resultType / endingId の内部値露出は既知課題として残る（escape-hatch）。 */}
        <p className="eyebrow">成果</p>
        <h3>{waiting.step.title ?? '成果'}</h3>
        <p className="interaction-card__meta">結果: {waiting.step.resultType}</p>
        <label className="input-label">
          結末の記録先
          <input value={endingId} onChange={(event) => setEndingId(event.target.value)} disabled={isBusy} />
        </label>
        <button
          className="primary-btn"
          disabled={isBusy}
          onClick={() => {
            void onCompleteReward(endingId);
          }}
        >
          記録して進む
        </button>
      </div>
    </div>
  );
}

function FreeInputOverlay({
  waiting,
  isBusy,
  error,
  onSubmitFreeInput,
  onSkipFreeInput
}: {
  waiting: Extract<WaitingState, { type: 'free_input' }>;
  isBusy: boolean;
  error?: string | null;
  onSubmitFreeInput: (payload: {
    userText: string;
    suggestedRouteKey?: string;
    inputMeta?: {
      charCount: number;
      isSilent?: boolean;
      isTooShort?: boolean;
      isNonsense?: boolean;
    };
  }) => Promise<void>;
  onSkipFreeInput: (routeKey?: string) => Promise<void>;
}) {
  const maxChars = waiting.step.maxChars ?? 200;
  const [text, setText] = useState('');
  const [selectedRouteKey, setSelectedRouteKey] = useState<string>('__auto__');
  const [failureCount, setFailureCount] = useState(0);
  const submitSeqRef = useRef(0);
  const lastCountedSeqRef = useRef(0);

  useEffect(() => {
    // Stepが変わったらリセット
    setText('');
    setSelectedRouteKey('__auto__');
    setFailureCount(0);
    submitSeqRef.current = 0;
    lastCountedSeqRef.current = 0;
  }, [waiting.step.id]);

  useEffect(() => {
    // submit後にerrorが出たら失敗回数としてカウント
    if (isBusy) return;
    if (!error) return;
    if (submitSeqRef.current <= lastCountedSeqRef.current) return;
    lastCountedSeqRef.current = submitSeqRef.current;
    setFailureCount((c) => c + 1);
  }, [isBusy, error]);

  const trimmed = text.trim();
  const charCount = text.length;
  const isTooLong = charCount > maxChars;

  const submitWithRouteKey = (routeKey: string) => {
    submitSeqRef.current += 1;
    const isSilent = true;
    void onSubmitFreeInput({
      userText: '',
      suggestedRouteKey: routeKey,
      inputMeta: { charCount: 0, isSilent, isTooShort: false, isNonsense: false }
    });
  };

  const classify = () => {
    const isSilent = trimmed.length === 0;
    const justSymbols = trimmed.length > 0 && /^[\p{P}\p{S}\s]+$/u.test(trimmed);
    const repeated = trimmed.length >= 4 && /^(.)(\1)+$/u.test(trimmed);
    const isTooShort = !isSilent && trimmed.length < 3;
    const isNonsense = !isSilent && (justSymbols || repeated);

    const suggestedRouteKey = (() => {
      if (isSilent) return waiting.step.emptyRouteKey ?? 'no_input';
      if (isNonsense) return 'derail';
      if (/[?？]/.test(trimmed) || /(犯人|矛盾|証拠|動機|トリック|推理)/.test(trimmed)) return 'insight';
      return waiting.step.defaultRouteKey ?? 'default';
    })();

    return {
      suggestedRouteKey,
      inputMeta: { charCount, isSilent, isTooShort, isNonsense }
    };
  };

  // P8-3「シェルの声」: 端末の状態断片（S1・「……〜」前置）＋丁寧の依頼形（S2）。
  // 旧文言はタメ口（第4のレジスタ）だったため一掃（guide §4.6）。
  const failureMessage =
    failureCount === 1
      ? '……聞き取れない。もう一度入力してください。'
      : failureCount >= 2
        ? '……接続が不安定。右上の案内を確かめつつ、先へ進むこともできます。'
        : null;

  return (
    <div className="interaction-overlay free-input-overlay">
      <div className="free-input-card">
        {/* Header */}
        <div className="free-input-header">
          <span className="free-input-icon" aria-hidden="true">
            <MessageCircle size="1em" />
          </span>
          <div className="free-input-title-area">
            <h3 className="free-input-title">{waiting.step.title || '自由入力'}</h3>
            <p className="free-input-prompt">
              {failureMessage ?? waiting.step.promptText ?? '思ったことを、ひとこと聞かせてください。'}
            </p>
          </div>
        </div>

        {/* Routes (optional) */}
        {Array.isArray((waiting.step as any).routes) && (waiting.step as any).routes.length > 0 && (
          <div className="free-input-routes">
            <p className="free-input-routes-label">選択肢から選ぶ（任意）</p>
            <div className="free-input-routes-grid">
              {(waiting.step as any).routes.map((r: any) => (
                <button
                  key={r.routeKey}
                  className={`free-input-route-btn ${selectedRouteKey === r.routeKey ? 'selected' : ''}`}
                  type="button"
                  disabled={isBusy}
                  onClick={() => {
                    setSelectedRouteKey(r.routeKey);
                    submitWithRouteKey(r.routeKey);
                  }}
                >
                  <span className="route-label">{r.label ?? r.routeKey}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="free-input-divider">
          <span>または自由に入力</span>
        </div>

        {/* Textarea */}
        <div className="free-input-textarea-wrapper">
          <textarea
            className="free-input-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={isBusy}
            rows={3}
            placeholder={waiting.step.inputPlaceholder ?? '推理を入力（任意）'}
          />
          <div className="free-input-char-count">
            <span className={isTooLong ? 'over-limit' : ''}>{charCount}</span>/{maxChars}
          </div>
        </div>
        {isTooLong && <p className="free-input-error">{maxChars}文字以内で入力してください。</p>}

        {/* Submit */}
        <div className="free-input-actions">
          <button
            className="free-input-submit-btn"
            type="button"
            disabled={isBusy || isTooLong}
            onClick={() => {
              submitSeqRef.current += 1;
              const { suggestedRouteKey, inputMeta } = classify();
              const finalRouteKey = selectedRouteKey === '__auto__' ? suggestedRouteKey : selectedRouteKey;
              void onSubmitFreeInput({ userText: text, suggestedRouteKey: finalRouteKey, inputMeta });
            }}
          >
            {isBusy ? '送信中……' : '送信する'}
          </button>
          {failureCount >= 2 && (
            <button
              className="free-input-skip-btn"
              type="button"
              disabled={isBusy}
              onClick={() => {
                void onSkipFreeInput(waiting.step.defaultRouteKey ?? 'default');
              }}
            >
              このまま先へ進む
            </button>
          )}
        </div>
      </div>
    </div>
  );
}



